import React, { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';

import { buyActions, useStore } from '@modules';

import Dropdown from '@components/Dropdown';

import Input from './Input';
import Title from './Title';
import ArrowIcon from './arrow_converter.svg';
import { useCustomContext } from '../context';
import { DataElementType, InputKeys } from '../types';

import style from './PaymentSelect.scss';

const cn = bemCssModules(style, 'PaymentSelect');
const ti = getTestId('PaymentSelect');

export const PaymentSelect: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const [_, dispatch] = useStore();
  const [search, setSearch] = useState('');
  const [isSearch, setIsSearch] = useState(false);

  const { active, setMethod, left, right, leftData, rightData, switchMethods, setActive } =
    useCustomContext();

  const leftInput = useRef<HTMLLabelElement>(null);
  const rightInput = useRef<HTMLLabelElement>(null);

  const data = active === 'right' ? rightData : leftData;
  const rightDataDisabled = rightData.length === 1 && rightData[0].list.length <= 1;

  const filter = (option: DataElementType) => {
    const { label = '', fullname = '', group } = option;
    return (
      label.toLowerCase().includes((search || '').toLowerCase()) ||
      fullname.toLowerCase().includes((search || '').toLowerCase()) ||
      group.toLowerCase().includes((search || '').toLowerCase())
    );
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleFocus = (name: InputKeys) => {
    setSearch('');
    setIsSearch(true);
    setActive(name);
  };
  const handleBlur = () => {
    setIsSearch(false);
  };

  const handleSetMethod = (method: DataElementType) => {
    setMethod(method);
    if (active === 'left' && method.value !== left.value) {
      dispatch(buyActions.setCvv(''));
    }
  };

  const handleSwitchMethods = () => {
    switchMethods();
    dispatch(buyActions.setCvv(''));
  };

  const list = Object.values(data)
    .map(group => ({
      key: group.key,
      list: group.list.filter(filter),
    }))
    .reduce((acc, group) => {
      if (!group.list.length) return acc;

      acc[group.key] = group.list;
      return acc;
    }, {});

  const isActiveLeftField = active === 'left' && isSearch;
  const isActiveRightField = active === 'right' && isSearch;

  const valueLeft = isActiveLeftField ? search : left?.label;
  const valueRight = isActiveRightField ? search : right?.label;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <Title className={cn('title')} />
      <Input
        onFocus={handleFocus}
        onBlur={handleBlur}
        isActive={isActiveLeftField}
        ref={leftInput}
        name="left"
        label={left?.inputLabel}
        value={valueLeft}
        onChange={handleSearch}
        testId={ti('from')}
      />
      <button
        type="button"
        onClick={handleSwitchMethods}
        data-testid={ti('change_direction_button')}
      >
        <ArrowIcon />
      </button>
      <Input
        ref={rightInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isActive={isActiveRightField}
        name="right"
        label={right?.inputLabel}
        value={valueRight}
        disabled={rightDataDisabled}
        onChange={handleSearch}
        testId={ti('to')}
      />
      <Dropdown<DataElementType>
        className={cn('dropdown')}
        data={list}
        isSearch
        search={search}
        placeholderSearch={t('payments_input.select')}
        onSearch={handleSearch}
        controller={rightDataDisabled ? [leftInput] : [leftInput, rightInput]}
        emptyText={t('payment_select.empty')}
        onChange={handleSetMethod}
        classNames={{
          group: cn('group'),
          groupTitle: cn('group-title'),
          defaultOption: cn('option'),
          label: cn('label-wrapper'),
        }}
        customLabel={el => (
          <div className={cn('label')} data-testid={ti(el.newCard ? 'new_card' : 'option')}>
            <div>{el.fullname || el.label}</div>
            {el.newCard && <div className={cn('plus-icon')}>+</div>}
            {el.additionalData && <small>{el.additionalData}</small>}
          </div>
        )}
      />
    </div>
  );
};

export default PaymentSelect;
